export const EXHIBITION = {
  title: "Première Ligne",
  subtitle: "First Line: A People's Witness",
  openingDate: new Date("2027-04-02T19:00:00-04:00"),
  dateRange: "April 2 — June 19, 2027",
  location: "Chamberbrook Arts District",
  address: "Chamberlayne Avenue, Richmond, Virginia",
  city: "Richmond, Virginia",
  artist: "Abdou Karim Ndoye",
  artistRole: "Photographer & Witness",
};

export const ARTIST_BIO = {
  name: "Abdou Karim Ndoye",
  short:
    "A Senegalese photographer who turned his camera on his own country at a moment of consequence.",
  long: `Abdou Karim Ndoye is a Senegalese photojournalist. In the spring of 2024 he set down his daily commissions and walked, with a single camera, through the streets of Dakar. He stayed for eleven months.

He works in black and white. He learned to see in the company of Seydou Keïta and Philip Jones Griffiths. He insists that dignity, never spectacle, remain the centre of the frame.

"First Line" is what he carried home. Portraits of students, market women, imams, farmers, lawyers, taxi drivers, mothers. The people who, in March 2024, became the country's slowest and most patient vote. The work has been shown in Bamako and at the Musée Théodore-Monod before arriving in Richmond.`,
  quote:
    "I was not covering the news. I was witnessing history. There is a difference.",
  quoteAttribution: "— Abdou Karim Ndoye, Dakar",
};

export const GALLERY = [
  {
    src: "/gallery/dsc03396_200x132.jpg",
    title: "Burning Tires, Moving Bodies",
    location: "Dakar · March 2024 · 200 × 132 cm",
    span: "col-span-2 row-span-2",
  },
  {
    src: "/gallery/dsc03151_90x60.jpg",
    title: "Flag and Raised Fists",
    location: "Avenue Bourguiba, Dakar · 90 × 60 cm",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/dsc03365_140x95.jpg",
    title: "Through the Smoke",
    location: "Smouldering barricade, Dakar · 140 × 95 cm",
    span: "col-span-1 row-span-2",
  },
  {
    src: "/gallery/dsc03190_90x60.jpg",
    title: "A Line of Riot Police",
    location: "Gendarmerie Nationale, Dakar · 90 × 60 cm",
    span: "col-span-1 row-span-1",
  },
  {
    src: "/gallery/dsc08193.jpg",
    title: "After the Fire",
    location: "Incinerated pickup, Dakar · March 2024",
    span: "col-span-2 row-span-1",
  },
];

// Team — in the order requested by the client.
// NOTE: Yaya.jpg has been assigned to Mamy Fall (assuming "Yaya" is her
// nickname — common in Senegal). Correct if wrong.
export const TEAM = [
  {
    name: "Aisha Bullard",
    role: "Founder & CEO",
    location: "Dakar / Richmond",
    photo: "/team/aisha.jpg",
  },
  {
    name: "Fatima Diallo",
    role: "Co-Executive Producer",
    location: "Dakar",
    photo: "/team/fatima.jpg",
  },
  {
    name: "Tevera Stith",
    role: "Executive Producer, US",
    location: "Washington, DC",
    photo: "/team/tevera.jpg",
  },
  {
    name: "Lydia Hickman",
    role: "Co-Executive Producer",
    location: "Dakar",
    photo: "/team/lydia.jpg",
  },
  {
    name: "Abdoulaye Samate",
    role: "Project Coordinator",
    location: "Richmond",
    photo: "/team/abdoulayeheadshot.jpg",
  },
  {
    name: "Mamy Fall",
    role: "Dakar Lead",
    location: "Dakar",
    photo: "/team/yaye.jpg",
  },
  {
    name: "Lena Bullard",
    role: "Research & Academic Partnerships",
    location: "Richmond",
    photo: "/team/lena.jpg",
  },
];

export const BLOG_POSTS = [
  {
    id: "letter-from-the-curator",
    date: "November 14, 2026",
    category: "Letter",
    title: "Why Richmond. Why Now.",
    kicker: "A Letter from the Curator",
    excerpt:
      "On why Richmond, on why now, and on what it asks of us to host a body of work made a continent away.",
    cover: "/field-notes/curator.jpg",
    alt: "The Chamberbrook gallery interior, a space waiting to hold the exhibition",
    body: [
      "When we first saw Abdou Karim Ndoye's photographs, we knew they belonged somewhere that could hold their weight. Not just a gallery wall. A place with its own history of struggle, resilience, and transformation.",
      "Richmond is that place.",
      "This city knows what it means to reckon with the past while building toward the future. It knows what it means to have a story that isn't finished. And it knows what it means to welcome a story from somewhere else and recognize it as your own.",
      "Première Ligne is not a Senegalese story being told in America. It is a human story finding a new home in a city that understands what that means.",
      "We are grateful to Chamberbrook, to Richmond, and to everyone who has opened doors, asked questions, and believed in this work before it even had walls to hang on.",
    ],
    signature: "— The Curatorial Team",
    readMoreLabel: "Read the full letter",
  },
  {
    id: "conversation-with-the-artist",
    date: "May 2026",
    category: "Interview",
    title: "Three Years, a Camera, and the Trust of the Frontline",
    kicker: "In Conversation: Abdou Karim Ndoye",
    excerpt:
      "On documenting for the next generation, the ethics of standing close, and the trust it takes to be handed a moment.",
    cover: "/field-notes/artist.jpg",
    alt: "Abdou Karim Ndoye, photographer and witness to Senegal's democratic movement",
    body: [
      {
        heading: "On documenting for the next generation",
        text: "Abdou Karim Ndoye does not think of himself as an artist first. He thinks of himself as a witness. His photographs are not meant to decorate walls. They are meant to inform the future. He captures images with a vision: that one day, young people will look at these frames and understand what it meant to be there. History, he reminds us, is not just what happened. It is what we choose to remember. And it matters who does the remembering.",
      },
      {
        heading: "On the ethics of standing close",
        text: "During the protests, Ndoye was not behind the police line. He was on the frontline, breathing the teargas, running with the youth, standing where the women stood. He was in Casamance when no one else had access, not the police, not journalists, not the international press. He was there because the people trusted him. That trust was not given lightly. It was earned, frame by frame, by showing up and staying close.",
      },
      {
        text: "Ndoye shares that he sees beyond the camera. What he witnessed was not just unrest. It was resilience, courage, and leadership, unwavering during one of the most difficult periods in Senegal's recent history. And what it led to, President Diomaye Faye elected in just ten days, taught him that everything is possible in life, but change does not come without a fight.",
      },
      {
        quote:
          "You cannot photograph people in moments of their lives and not feel the weight of that trust. I do not take images. I receive them. And I carry them with me long after the shutter closes.",
      },
    ],
    readMoreLabel: "Read the full interview",
  },
  {
    id: "the-march-2024-transformation",
    date: "August 21, 2026",
    category: "Context",
    title: "March 2024, Briefly",
    kicker: "For Visitors Arriving Without Context",
    excerpt:
      "A short primer for visitors arriving without context: what happened in Senegal, what it meant, and what remains unfinished.",
    cover: "/field-notes/context.jpg",
    alt: "A moment from the streets of Dakar during the March 2024 democratic movement",
    body: [
      "March 2024. Dakar.",
      "A country held its breath. The streets filled with people who refused to be silent. A young democracy, still learning what it means to be free, tested its own limits.",
      "Abdou Karim Ndoye was there. Not as a journalist. Not as a commentator. As a witness. His camera became a way of holding the moment steady, so others could see it later.",
      "What he captured, the faces, the streets, the flags, the hope, became the visual record of a people refusing to accept what they were told was inevitable.",
      "Ten days after walking out of prison, Bassirou Diomaye Faye was elected President.",
      "That is what we are showing. Not just photographs. A moment that changed a nation.",
    ],
    signature: "— The Curatorial Team",
    readMoreLabel: "Read the full piece",
  },
];
